import {
  CATEGORIES,
  CHIPS,
  DAYS_ACTIVE,
  DEFAULT_FILTERS,
  FILTERS_ADDITIONAL_OPTIONS,
  FILTERS_ADDITIONAL_OPTIONS_NAMES,
  FILTERS_AS_MULTI_SELECT_MAP,
  FILTERS_PARAMS_TYPES,
  FILTERS_TYPE_OUTPUT_TEXT,
  GENERAL,
  IGNORE_FILTERS_DATA,
  INPUT,
  PLACEMENT_DATE,
  SEARCH_BY_TEXT_MAP,
  SORTING,
} from '@/common/constants/filtersFields'
import {
  addZeroPad,
  getDataWithoutEmptyValues,
  wrapWordsInSpan,
} from '@/common/utils/index'
import qs from 'qs'
import {
  REGEXP_REMOVE_BRACKETS,
  REGEXP_SPLIT_INPUT_FILTER,
} from '@/common/constants/regExp'
import baseLogger from '@/common/services/BaseLogger'

export const queryToFiltersData = (query) => {
  if (!query) return

  const result = {}
  const pairs = query.split('&')

  for (const pair of pairs) {
    const [key, value] = pair.split('=')
    if (key && value !== undefined) {
      const decodedKey = decodeURIComponent(key)
      const decodedValue = decodeURIComponent(value)

      if (decodedKey.endsWith('[]')) {
        const arrayKey = decodedKey.slice(0, -2)
        if (!result[arrayKey]) {
          result[arrayKey] = []
        }
        result[arrayKey].push(decodedValue)
      } else {
        result[decodedKey] = decodedValue
      }
    }
  }

  return result
}

const getTransformedCategories = (categories) => {
  return {
    ...categories,
    type: CHIPS,
  }
}

const getTransformedMultiplySelect = (fields, data) => {
  const updatedFields = fields.map((field) => {
    return {
      label: field.name,
      value: field.value,
    }
  })

  return {
    ...data,
    fields: updatedFields,
  }
}

export const getTransformedFiltersConfig = (filters) => {
  return filters.map((filter) => {
    const updatedData = Object.keys(filter.data).map((key) => {
      let currentData = filter.data[key]

      if (key === CATEGORIES) {
        currentData = getTransformedCategories(currentData)
      }

      if (filter.name === GENERAL) {
        delete filter?.data?.ad_geo?.modes
      }

      if (FILTERS_AS_MULTI_SELECT_MAP[currentData.type]) {
        currentData = getTransformedMultiplySelect(
          currentData.fields,
          currentData
        )
      }

      return {
        ...currentData,
        name: key,
        ...(currentData.name && { label: currentData.name }),
      }
    })

    return {
      ...filter,
      data: updatedData,
    }
  })
}

export const getInitialFiltersParams = (filters) => {
  const mappedFilters = filters.reduce((acc, next) => {
    const updateData = next.data.reduce((acc, next) => {
      const type = next.type
      const name = next.name
      acc[name] = FILTERS_PARAMS_TYPES[type]

      if (name === SORTING) {
        acc[name] = {
          ...acc[name],
          data: 'creation_date',
        }
      }

      return acc
    }, {})

    return {
      ...acc,
      ...updateData,
    }
  }, {})

  return {
    ...mappedFilters,
    ...FILTERS_ADDITIONAL_OPTIONS,
  }
}

export const getInitialClassicFiltersParams = (data) => {
  const mappedFilters = Object.keys(data).reduce((acc, key) => {
    const item = data[key]

    acc[key] = FILTERS_PARAMS_TYPES[item.type]

    if (key === SORTING) {
      acc[key] = {
        ...acc[key],
        data: 'creation_date',
      }
    }

    return acc
  }, {})

  return {
    ...mappedFilters,
    ...FILTERS_ADDITIONAL_OPTIONS,
  }
}

export const getQueryUrlFromData = (data) => {
  return qs.stringify(data, {
    arrayFormat: 'brackets',
    strictNullHandling: true,
    encode: false,
    indices: true,
    allowDots: true,
  })
}

const createObjectByKey = (key, value, isModified = true) => {
  const keys = key.split('.')
  const result = {}

  let currentLevel = result
  const isSingleItem = keys.length <= 1

  for (let i = 0; i < keys.length; i++) {
    const isLastKey = i === keys.length - 1

    if (!isModified) {
      currentLevel[key] = Boolean(value)
    } else if (isLastKey && isSingleItem && isModified) {
      const intValue = parseInt(value)
      const correctValue = !isNaN(intValue) ? intValue : value

      currentLevel[keys[i]] = {}
      currentLevel[keys[i]].data = correctValue
    } else if (isLastKey) {
      currentLevel[keys[i]] = value
    } else {
      currentLevel[keys[i]] = {}
      currentLevel = currentLevel[keys[i]]
    }
  }

  return result
}

export const parseQuery = (queryString) => {
  const params = new URLSearchParams(queryString)
  const queryObject = {}

  for (const [key, value] of params.entries()) {
    if (queryObject[key]) {
      queryObject[key] = Array.isArray(queryObject[key])
        ? [...queryObject[key], value]
        : [queryObject[key], value]
    } else {
      queryObject[key] = value
    }
  }

  return queryObject
}

export const createQueryRequestParams = (data) => {
  const mappedData = Object.keys(data).reduce((acc, key) => {
    const current = data[key]

    if (SEARCH_BY_TEXT_MAP[key] && Array.isArray(current.value)) {
      acc[key] = {
        ...current,
        value: current.value.join(' '),
      }
    } else {
      acc[key] = current
    }

    return acc
  }, {})

  return createQueryParams(mappedData)
}

export const createQueryParams = (data) => {
  if (!data) return {}

  return Object.keys(data).reduce((acc, key) => {
    const current = data[key]

    let isMultiplyData = false

    if (Array.isArray(current)) {
      isMultiplyData = current.length
    } else if (current && typeof current === 'object') {
      isMultiplyData = Object.keys(current).length > 1
    } else if (typeof current === 'string') {
      isMultiplyData = current.length
    }

    const filteredData = getDataWithoutEmptyValues(current)
    let correctData = isMultiplyData ? filteredData : filteredData?.data
    if (FILTERS_ADDITIONAL_OPTIONS_NAMES[key]) {
      correctData = filteredData
    }

    if (DEFAULT_FILTERS[key] === correctData || !correctData) {
      return acc
    }

    return {
      ...acc,
      ...{ [key]: correctData },
    }
  }, {})
}

export const transformQueryToFilterParams = (query, isAvailableNumbers) => {
  return Object.keys(query).reduce((acc, key) => {
    const isMustArray = key.endsWith('[]') || key.endsWith('[0]')
    let correctKey = key.replace(REGEXP_REMOVE_BRACKETS, '')
    const keys = correctKey.split('.')
    const initialKey = keys[0]
    let isModified = true

    let current = query[key]

    if (isMustArray && !Array.isArray(current)) {
      current = [current]
    }

    if (isAvailableNumbers && Array.isArray(current)) {
      current = current.map((item) => {
        const value = parseInt(item)
        return isNaN(value) ? item : value
      })
    }

    if (FILTERS_ADDITIONAL_OPTIONS_NAMES[correctKey]) {
      isModified = false
    }

    const newData = createObjectByKey(correctKey, current, isModified)

    if (acc[initialKey]) {
      acc[initialKey] = {
        ...acc[initialKey],
        ...newData[initialKey],
      }

      return acc
    }

    return {
      ...acc,
      ...newData,
    }
  }, {})
}

export const getPinnedFilters = (filters) => {
  return filters.reduce((acc, next) => {
    const pinnedFilters = next.data.reduce((acc, current) => {
      if (current.pinned) {
        acc.push(current)
      }

      return acc
    }, [])

    acc.push(...pinnedFilters)

    return acc
  }, [])
}

export const createFiltersConfigMap = (filters) => {
  const createdMap = filters.reduce((acc, next) => {
    const newData = next.data.reduce((acc, current) => {
      let currentData = {
        ...current,
        category: next.icon,
      }

      if (currentData.name === CATEGORIES) {
        currentData = {
          ...current,
          label: next.name,
        }
      }

      acc[current.name] = currentData
      return acc
    }, {})

    return {
      ...acc,
      ...newData,
    }
  }, {})
  return createdMap
}

const getCalculatedAppliedFilters = ({ data, name, label, type, category }) => {
  if ((name === SORTING && DEFAULT_FILTERS.sorting === data) || !data)
    return null

  let value = Object.keys(data).reduce((acc, key) => {
    const current = data[key]

    if (Array.isArray(current)) {
      acc += current.length
    } else {
      acc = current
    }

    return acc
  }, 0)

  if (name === DAYS_ACTIVE) {
    const separator = data.from && data.to ? '- ' : ''
    value = `${addZeroPad(data.from)}${separator}${addZeroPad(data.to)}`
  }

  if (name === PLACEMENT_DATE) {
    const correctEndDate = data.endDate ? `- ${data.startDate}` : ''
    value = `${data.startDate} ${correctEndDate}`
  }

  if (type === INPUT) {
    value = wrapWordsInSpan(data.value).count
  }

  return {
    name,
    label,
    value,
    type,
    category,
  }
}

const getFilterText = (filter, searchData) => {
  const item = filter.fields.find((field) => field.value === searchData.data)
  return item?.name
}

export const transformActiveFiltersChips = (data, config) => {
  const chips = Object.keys(config).reduce((accumulator, key) => {
    const current = config[key]

    let filter = getDataWithoutEmptyValues(data[current.name])

    if (IGNORE_FILTERS_DATA.includes(current.type) && filter) {
      delete filter.data
    }

    if (
      !filter ||
      !Object.keys(filter).length ||
      (current.name === SORTING && DEFAULT_FILTERS.sorting === filter?.data)
    )
      return accumulator
    if (FILTERS_TYPE_OUTPUT_TEXT.includes(current.type)) {
      filter.data = getFilterText(current, filter)
    }

    if (current.name === DAYS_ACTIVE) {
      filter.fromText = getFilterText(current, 'from')
      filter.ToText = getFilterText(current, 'to')
    }

    const value = getCalculatedAppliedFilters({
      data: filter,
      name: current.name,
      label: current?.label,
      type: current.type,
      category: current.category,
    })

    if (value) {
      accumulator.push(value)
    }

    return accumulator
  }, [])

  return chips
}

export const countFiltersApplied = (filters) => {
  const onlyFilters = Object.keys(filters ?? {}).filter(
    (filterName) => !SEARCH_BY_TEXT_MAP[filterName]
  )
  return onlyFilters.length
}

export const getCorrectFilterData = (data) => {
  let transformedData = data

  if (FILTERS_AS_MULTI_SELECT_MAP[data.type] && typeof data !== 'object') {
    transformedData = {
      data: data,
    }
  }

  return transformedData
}

export const getSearchBarFilters = (data) => {
  if (!data) return []

  return Object.keys(data).reduce((acc, next) => {
    const current = data[next]
    const filterName = SEARCH_BY_TEXT_MAP[next]

    if (filterName && Array.isArray(current.value)) {
      const mappedList = current.value.map((item) => ({
        value: item,
        type: next.split('_')[0],
        filter: next,
      }))
      acc.push(...mappedList)
    } else if (filterName && typeof current.value === 'string') {
      const createdList = wrapWordsInSpan(
        current.value,
        REGEXP_SPLIT_INPUT_FILTER
      ).list.map((item) => ({
        value: item.value,
        type: filterName,
        filter: next,
      }))

      acc.push(...createdList)
    }

    return acc
  }, [])
}

export const getMappedSearchBarList = (list, defaultFilters) => {
  const result = Object.keys(defaultFilters).reduce((acc, key) => {
    acc[key] = []
    return acc
  }, {})

  list.forEach((item) => {
    const { type } = item
    if (!result[type]) {
      result[type] = []
    }
    result[type].push(item.value)
  })

  return result
}

export const omitProperties = (source, propertiesToOmit) => {
  if (
    !source ||
    typeof source !== 'object' ||
    !Array.isArray(propertiesToOmit)
  ) {
    baseLogger.logger.error(
      'Invalid input: source must be an object and propertiesToOmit must be an array.'
    )
    return source
  }

  return Object.keys(source).reduce((accumulator, property) => {
    if (!propertiesToOmit.includes(property)) {
      accumulator[property] = source[property]
    }
    return accumulator
  }, {})
}
